/**
 * Theme store for dark/light mode management
 * @module stores/theme
 */

import { writable } from 'svelte/store';

const THEME_KEY = 'webdeploy-theme';

/**
 * Get initial theme from localStorage or system preference
 * @returns {'dark' | 'light'}
 */
function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';

  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'dark' || stored === 'light') {
    return stored;
  }

  // Default to dark mode (better for neon colors)
  return 'dark';
}

/**
 * Apply theme to document
 * @param {'dark' | 'light'} theme
 */
function applyTheme(theme) {
  if (typeof document === 'undefined') return;

  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }

  localStorage.setItem(THEME_KEY, theme);
  console.log(`[Theme] Applied theme: ${theme}`);
}

// Create the store
function createThemeStore() {
  const { subscribe, set, update } = writable(getInitialTheme());

  return {
    subscribe,

    /**
     * Toggle between dark and light mode
     */
    toggle: () => {
      update(current => {
        const newTheme = current === 'dark' ? 'light' : 'dark';
        applyTheme(newTheme);
        return newTheme;
      });
    },

    /**
     * Set specific theme
     * @param {'dark' | 'light'} theme
     */
    set: (theme) => {
      applyTheme(theme);
      set(theme);
    },

    /**
     * Initialize theme on mount
     */
    init: () => {
      const theme = getInitialTheme();
      applyTheme(theme);
      set(theme);
    }
  };
}

export const theme = createThemeStore();

// Derived store for checking if dark mode
export const isDark = {
  subscribe: (fn) => theme.subscribe(t => fn(t === 'dark'))
};
